import { AfterViewInit, Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { SalaryHistoryService } from 'src/app/services/salary-history.service';
import { UtilsService } from 'src/app/services/utils.service';
import { PermissionService } from 'src/app/services/permission.service';
import {
    Chart,
    LineController,
    LineElement,
    PointElement,
    LinearScale,
    CategoryScale,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';

declare const $: any;

Chart.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Legend, Filler);

@Component({
    selector: 'app-salary-history-tab',
    templateUrl: './salary-history-tab.component.html',
    styleUrls: ['./salary-history-tab.component.css', '../employee-tab-shared.css']
})
export class SalaryHistoryTabComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() employee: any;
    @Input() variant: 'dashboard' | 'compact' = 'dashboard';

    @ViewChild('salaryChartCanvas') salaryChartCanvas?: ElementRef<HTMLCanvasElement>;

    readonly movementTypes: string[] = ['Alta', 'Aumento', 'Ajuste', 'Revisión anual', 'Otro'];
    readonly pageSizeOptions: number[] = [10, 25, 50];
    readonly dateRangeOptions: string[] = ['Ultimos 12 meses', 'Ultimos 6 meses', 'Este año', 'Todo el historial'];

    rows: any[] = [];
    loading = false;
    loadError = false;

    searchTerm = '';
    actionsOpen = false;
    pageSize = 10;
    currentPage = 1;
    dateRange = 'Ultimos 12 meses';

    form!: FormGroup;
    submitted = false;

    private chart: Chart<'line'> | null = null;

    constructor(
        private fb: FormBuilder,
        private salaryHistoryService: SalaryHistoryService,
        private utils: UtilsService,
        private spinner: NgxSpinnerService,
        private permissionService: PermissionService
    ) { }

    canDo(action: string): boolean {
        return this.permissionService.canDo('Listado de colaboradores', action);
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            movement_type: ['', Validators.required],
            movement_date: ['', Validators.required],
            daily_salary: ['', Validators.required],
            imss_sdi: [''],
            fixed_part: [''],
            variable_part: ['']
        });
        this.fetch();
    }

    ngAfterViewInit(): void {
        this.renderChart();
    }

    ngOnDestroy(): void {
        this.chart?.destroy();
    }

    get f() {
        return this.form.controls;
    }

    get filteredRows(): any[] {
        const term = this.searchTerm.trim().toLowerCase();
        if (!term) return this.rows;
        return this.rows.filter(row =>
            (row.movement_type || '').toLowerCase().includes(term) ||
            this.formatDate(row.movement_date).includes(term)
        );
    }

    get totalPages(): number {
        return Math.max(1, Math.ceil(this.filteredRows.length / this.pageSize));
    }

    get pagedRows(): any[] {
        const start = (this.currentPage - 1) * this.pageSize;
        return this.filteredRows.slice(start, start + this.pageSize);
    }

    get rangeStartDate(): Date | null {
        const now = new Date();
        switch (this.dateRange) {
            case 'Ultimos 12 meses': return new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
            case 'Ultimos 6 meses': return new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
            case 'Este año': return new Date(now.getFullYear(), 0, 1);
            default: return null;
        }
    }

    get rangedRows(): any[] {
        const start = this.rangeStartDate;
        const sorted = [...this.rows].sort((a, b) => new Date(a.movement_date).getTime() - new Date(b.movement_date).getTime());
        if (!start) return sorted;
        return sorted.filter(row => new Date(row.movement_date) >= start);
    }

    get latestRow(): any {
        if (!this.rows.length) return null;
        return [...this.rows].sort((a, b) => new Date(b.movement_date).getTime() - new Date(a.movement_date).getTime())[0];
    }

    get daysSinceLastMovement(): string {
        const latest = this.latestRow;
        if (!latest) return '--';
        const diffMs = new Date().getTime() - new Date(latest.movement_date).getTime();
        const days = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
        return `Hace ${days} día${days === 1 ? '' : 's'}`;
    }

    onDateRangeChange(): void {
        this.renderChart();
    }

    onSearchChange(): void {
        this.currentPage = 1;
    }

    onPageSizeChange(size: string): void {
        this.pageSize = Number(size);
        this.currentPage = 1;
    }

    prevPage(): void {
        if (this.currentPage > 1) this.currentPage--;
    }

    nextPage(): void {
        if (this.currentPage < this.totalPages) this.currentPage++;
    }

    toggleActionsDropdown(): void {
        this.actionsOpen = !this.actionsOpen;
    }

    closeActionsDropdown(): void {
        this.actionsOpen = false;
    }

    @HostListener('document:click')
    onDocumentClick(): void {
        this.closeActionsDropdown();
    }

    exportCsv(): void {
        this.closeActionsDropdown();
        const headers = ['Tipo de movimiento', 'Fecha del movimiento', 'Salario diario', 'SDI IMSS', 'Parte fija', 'Parte variable', 'Usuario'];
        const escape = (value: any) => {
            const str = String(value ?? '');
            return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
        };
        const source = this.variant === 'dashboard' ? this.rangedRows : this.filteredRows;
        const lines = [headers.join(',')];
        source.forEach(row => {
            lines.push([
                row.movement_type,
                this.formatDate(row.movement_date),
                row.daily_salary,
                row.imss_sdi,
                row.fixed_part,
                row.variable_part,
                row.user?.name
            ].map(escape).join(','));
        });
        const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `historial-salarios-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    }

    fetch(): void {
        if (!this.employee?.id) return;
        this.loading = true;
        this.loadError = false;
        this.salaryHistoryService.list(this.employee.id).subscribe({
            next: (resp: any) => {
                this.rows = Array.isArray(resp) ? resp : (Array.isArray(resp?.data) ? resp.data : []);
                this.currentPage = 1;
                this.loading = false;
                this.renderChart();
            },
            error: () => {
                this.rows = [];
                this.loading = false;
                this.loadError = true;
            }
        });
    }

    openAddModal(): void {
        this.submitted = false;
        this.form.reset({ movement_type: '', movement_date: '', daily_salary: '', imss_sdi: '', fixed_part: '', variable_part: '' });
        $('#salaryMovementModal').modal('show');
    }

    closeAddModal(): void {
        $('#salaryMovementModal').modal('hide');
    }

    save(): void {
        this.submitted = true;
        if (this.form.invalid || !this.employee?.id) {
            this.form.markAllAsTouched();
            return;
        }

        const raw = this.form.value;
        const payload: any = {
            movement_type: raw.movement_type,
            movement_date: raw.movement_date,
            daily_salary: raw.daily_salary,
            imss_sdi: raw.imss_sdi || null,
            fixed_part: raw.fixed_part || null,
            variable_part: raw.variable_part || null
        };

        this.spinner.show();
        this.salaryHistoryService.add(this.employee.id, payload).subscribe({
            next: () => {
                this.spinner.hide();
                this.closeAddModal();
                this.utils.alertSwal({ title: 'Operación exitosa', text: 'El movimiento de salario se registró correctamente', icon: 'success' });
                this.fetch();
            },
            error: () => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo registrar el movimiento de salario', icon: 'error' });
            }
        });
    }

    formatCurrency(value: any): string {
        if (value === null || value === undefined || value === '') return '--';
        const num = Number(value) || 0;
        return num.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
    }

    formatDate(value: any): string {
        if (!value) return '--/--/----';
        const datePart = String(value).split('T')[0].split(' ')[0];
        const [yyyy, mm, dd] = datePart.split('-');
        if (!yyyy || !mm || !dd) return '--/--/----';
        return `${dd}/${mm}/${yyyy}`;
    }

    private renderChart(): void {
        if (this.variant !== 'dashboard') return;
        setTimeout(() => {
            const canvas = this.salaryChartCanvas?.nativeElement;
            if (!canvas) return;

            const rows = this.rangedRows;
            const labels = rows.map(row => this.formatDate(row.movement_date));
            const data = rows.map(row => Number(row.daily_salary) || 0);

            if (this.chart) {
                this.chart.data.labels = labels;
                this.chart.data.datasets[0].data = data;
                this.chart.update();
                return;
            }

            const ctx = canvas.getContext('2d');
            if (!ctx) return;

            this.chart = new Chart(ctx, {
                type: 'line',
                data: {
                    labels,
                    datasets: [{
                        label: 'Salario diario',
                        data,
                        borderColor: '#1447e6',
                        backgroundColor: 'rgba(20, 71, 230, 0.08)',
                        pointBackgroundColor: '#1447e6',
                        pointBorderColor: '#fff',
                        pointRadius: 5,
                        pointHoverRadius: 6,
                        borderWidth: 2,
                        tension: 0.2,
                        fill: true
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            backgroundColor: '#fff',
                            titleColor: '#1d1d1f',
                            bodyColor: '#101828',
                            borderColor: '#e1e8ed',
                            borderWidth: 1,
                            padding: 12,
                            callbacks: {
                                label: (item) => `Salario diario: ${this.formatCurrency(item.parsed.y)}`
                            }
                        }
                    },
                    scales: {
                        y: {
                            beginAtZero: true,
                            ticks: { color: '#94a3b8', font: { size: 16 }, callback: (value) => Number(value).toFixed(2) },
                            grid: { color: '#e2e8f0' }
                        },
                        x: {
                            ticks: { color: '#94a3b8', font: { size: 16 } },
                            grid: { display: false }
                        }
                    }
                }
            });
        });
    }
}
